import { useHandler } from "@redotech/react-util/hook";
import {
  DeliveryDistanceMilesSegment,
  SegmentableCheckoutCharacteristic,
  SegmentNumberOperator,
} from "@redotech/redo-conversion-core-common/checkouts/checkout-tree";
import {
  rcoDeliveryDistanceMilesSegmentSchema,
  RcoSegmentStep,
} from "@redotech/redo-flows-common/advanced-flow-db-parser";
import { useTranslation } from "@redotech/redo-i18n";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import { RedoBadge } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoIncrementDecrement } from "@redotech/redo-web/arbiter-components/increment-decrement/redo-increment-decrement";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import { PartialWithRequiredType } from "../..";

export const DeliveryDistanceMilesInspectorContent = memo(
  function DeliveryDistanceMilesInspectorContent({
    step,
  }: {
    step: RcoSegmentStep;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoDeliveryDistanceMilesSegmentSchema.parse(step.segment),
      [step],
    );

    return (
      <Flex align="flex-start" dir="column" gap="xs">
        <Text fontSize="xs" fontWeight="medium" textColor="secondary">
          {t("Condition")}
        </Text>
        <Text fontSize="xs" fontWeight="medium" textColor="primary">
          {t("Delivery distance is within {{count}} miles", {
            count: segment.miles,
          })}
        </Text>
        <UsOnlyWarningBadge />
      </Flex>
    );
  },
);

export const EditableDeliveryDistanceMilesInspectorContent = memo(
  function EditableDeliveryDistanceMilesInspectorContent({
    step,
    setStep,
  }: {
    step: Omit<RcoSegmentStep, "matchingNextId" | "nonMatchingNextId"> & {
      matchingNextId?: string | undefined;
      nonMatchingNextId?: string | undefined;
    };
    setStep: (step: PartialWithRequiredType<RcoSegmentStep>) => void;
  }) {
    const { t } = useTranslation();
    const segment = useMemo(
      () => rcoDeliveryDistanceMilesSegmentSchema.parse(step.segment),
      [step],
    );

    const handleMilesChange = useHandler((miles: number) => {
      const updatedSegment: DeliveryDistanceMilesSegment = {
        segmentableCharacteristic:
          SegmentableCheckoutCharacteristic.DELIVERY_DISTANCE_MILES,
        miles,
        operator: SegmentNumberOperator.LESS_THAN_OR_EQUAL,
      };
      setStep({ ...step, segment: updatedSegment });
    });

    return (
      <>
        <Flex align="flex-start" dir="column" gap="xs">
          <Text fontSize="xs" fontWeight="medium" textColor="secondary">
            {t("Condition")}
          </Text>
          <Text fontSize="xs" fontWeight="medium" textColor="primary">
            {t("Delivery distance is within")}
          </Text>
        </Flex>
        <RedoIncrementDecrement
          min={1}
          setValue={handleMilesChange}
          size="small"
          step={1}
          suffix={t("miles")}
          value={segment.miles}
        />
        <UsOnlyWarningBadge />
      </>
    );
  },
);

const UsOnlyWarningBadge = memo(function UsOnlyWarningBadge() {
  const { t } = useTranslation();
  return (
    <RedoBadge
      color="warning"
      segmentLeading={{ type: "icon", Icon: AlertCircleIcon }}
      size="sm"
      text={t("US domestic shipments only")}
    />
  );
});
